import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  BackupsFilterParams,
  BackupScheduleType,
  BackupsListResponse,
  BackupType,
  CreateBackupScheduleParams,
  UpdateBackupScheduleParams,
  CreateBackupParams
} from '@/types/backup'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchBackups,
  fetchBackup,
  createBackupSchedule,
  updateBackupSchedule,
  deleteBackupSchedule,
  bulkDeleteBackupSchedules,
  runBackupSchedule,
  triggerBackup
} from '@/lib/api'
import { SortEnum } from '@/enums'

type BackupStateType = {
  isLoading: boolean
  response: BackupScheduleType | null
  error: {
    status?: number
    message: string
  } | null
}

type BackupsListStateType = {
  isLoading: boolean
  response: BackupsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type BackupsStoreState = {
  // Filters state
  filters: BackupsFilterParams
  setFilters: (filters: Partial<BackupsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // List state
  backupsListState: BackupsListStateType
  fetchBackups: (params?: BackupsFilterParams) => Promise<BackupsListResponse>
  resetBackupsList: () => void

  // Single state
  backupState: BackupStateType
  getBackup: (id: string) => Promise<BackupScheduleType>
  createBackupSchedule: (params: CreateBackupScheduleParams) => Promise<BackupScheduleType>
  updateBackupSchedule: (id: string, params: UpdateBackupScheduleParams) => Promise<BackupScheduleType>
  deleteBackupSchedule: (id: string) => Promise<void>
  resetBackup: () => void

  // Selection operations
  selectedBackupIds: string[]
  toggleBackupSelection: (id: string) => void
  selectAllBackups: (backups: BackupScheduleType[]) => void
  deselectAllBackups: () => void
  isBackupSelected: (id: string) => boolean

  // Bulk delete
  bulkDeleteBackupSchedules: (ids: string[]) => Promise<BulkDeleteResponse>

  // Run / Trigger
  runBackupSchedule: (id: string) => Promise<void>
  triggerBackup: (params: CreateBackupParams) => Promise<BackupType>
}

const defaultFilters: BackupsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialBackupState: BackupStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialBackupsListState: BackupsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useBackupSchedulesStore = create<BackupsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    backupState: initialBackupState,
    backupsListState: initialBackupsListState,
    selectedBackupIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof BackupsFilterParams]
          } else {
            updatedFilters[key as keyof BackupsFilterParams] = value as any
          }
        })
        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: BackupsFilterParams = {}
      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as BackupsFilterParams['sort']
            break
          case 'enabled':
          case 'includeDatabase':
          case 'includeStorage':
            filters[key] = value === 'true'
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value.split(',').filter(Boolean)
            break
          default:
            filters[key as keyof BackupsFilterParams] = value as any
            break
        }
      })
      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })
      return params
    },

    // List operations
    fetchBackups: async (params?: BackupsFilterParams) => {
      const currentState = get().backupsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({ backupsListState: { ...initialBackupsListState, isLoading: true } }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchBackups(filtersToUse)
        set(() => ({ backupsListState: { ...initialBackupsListState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          backupsListState: {
            ...initialBackupsListState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetBackupsList: () => set(() => ({ backupsListState: initialBackupsListState })),

    // Single operations
    getBackup: async (id: string) => {
      const currentState = get().backupState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({ backupState: { ...initialBackupState, isLoading: true } }))

      try {
        const data = await fetchBackup(id)
        set(() => ({ backupState: { ...initialBackupState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          backupState: {
            ...initialBackupState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    createBackupSchedule: async (params: CreateBackupScheduleParams) => {
      set(() => ({ backupState: { ...initialBackupState, isLoading: true } }))
      try {
        const data = await createBackupSchedule(params)
        set(() => ({ backupState: { ...initialBackupState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          backupState: {
            ...initialBackupState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    updateBackupSchedule: async (id: string, params: UpdateBackupScheduleParams) => {
      set(() => ({ backupState: { ...initialBackupState, isLoading: true } }))
      try {
        const data = await updateBackupSchedule(id, params)
        set(() => ({ backupState: { ...initialBackupState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          backupState: {
            ...initialBackupState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    deleteBackupSchedule: async (id: string) => {
      set(() => ({ backupState: { ...get().backupState, isLoading: true } }))
      try {
        await deleteBackupSchedule(id)
        set(() => ({ backupState: initialBackupState }))
      } catch (error: any) {
        set(() => ({
          backupState: {
            ...initialBackupState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetBackup: () => set(() => ({ backupState: initialBackupState })),

    // Selection operations
    toggleBackupSelection: id => {
      set(state => {
        if (state.selectedBackupIds.includes(id)) {
          return { selectedBackupIds: state.selectedBackupIds.filter(bid => bid !== id) }
        }
        return { selectedBackupIds: [...state.selectedBackupIds, id] }
      })
    },

    selectAllBackups: backups => {
      set(() => ({ selectedBackupIds: backups.map(b => b.id) }))
    },

    deselectAllBackups: () => set(() => ({ selectedBackupIds: [] })),

    isBackupSelected: id => get().selectedBackupIds.includes(id),

    // Bulk delete
    bulkDeleteBackupSchedules: async ids => {
      set(state => ({ backupState: { ...state.backupState, isLoading: true, error: null } }))
      try {
        const response = await bulkDeleteBackupSchedules({ ids })
        set(state => ({
          backupState: { ...state.backupState, isLoading: false },
          selectedBackupIds: state.selectedBackupIds.filter(id => !response.deletedItems.includes(id))
        }))
        return response
      } catch (error: any) {
        set(state => ({
          backupState: {
            ...state.backupState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) },
            isLoading: false
          }
        }))
        throw error
      }
    },

    // Run / Trigger
    runBackupSchedule: async (id: string) => {
      await runBackupSchedule(id)
    },

    triggerBackup: async (params: CreateBackupParams) => {
      return await triggerBackup(params)
    }
  }))
)

export default useBackupSchedulesStore
